import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import ImageSlot from "../components/ImageSlot";
import { StatusTag } from "../components/WorkCard";
import { FadeUp, MaskedLine } from "../components/Reveal";
import { caseStudies } from "../data/content";

export default function CaseStudy() {
  const { slug } = useParams();
  const index = caseStudies.findIndex((s) => s.slug === slug);
  if (index === -1) return <Navigate to="/work" replace />;
  const study = caseStudies[index];
  const next = caseStudies[(index + 1) % caseStudies.length];

  return (
    <div className="pt-32 md:pt-44">
      <article className="mx-auto max-w-7xl px-6">
        <FadeUp>
          <Link
            to="/work"
            data-testid="case-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:text-blue"
          >
            <ArrowLeft size={14} /> All work
          </Link>
        </FadeUp>

        <header className="mt-10 border-b border-ink pb-12">
          <div className="flex flex-wrap items-center gap-3">
            <StatusTag status={study.status} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              {study.brand}{study.year ? ` · ${study.year}` : ""}
            </span>
          </div>
          <h1
            data-testid="case-title"
            className="mt-8 font-display font-extrabold leading-[0.95] tracking-[-0.03em] text-[clamp(2.6rem,7.5vw,6.5rem)]"
          >
            <MaskedLine delay={0.1}>{study.project.toUpperCase()}</MaskedLine>
          </h1>
          <FadeUp delay={0.25}>
            <p className="mt-8 max-w-2xl font-display text-xl font-medium leading-snug tracking-tight text-smoke md:text-2xl">
              {study.thought}
            </p>
          </FadeUp>
        </header>

        <div className="grid gap-6 border-b border-line py-8 sm:grid-cols-3">
          {[
            ["Client", study.brand],
            ["Role", study.role],
            ["Disciplines", study.tags.join(" · ")],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                {k}
              </p>
              <p className="mt-2 text-sm font-medium text-ink">{v}</p>
            </div>
          ))}
        </div>

        <FadeUp className="py-14 md:py-20">
          {study.image ? (
            <figure className="border border-line bg-fog p-3 md:p-4">
              <img
                src={study.image}
                alt={`${study.project} — campaign one-pager`}
                className="w-full"
                data-testid="case-hero-image"
              />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                Campaign board — {study.brand}
              </figcaption>
            </figure>
          ) : (
            <ImageSlot
              label={study.project}
              sub={`Hero campaign visual — ${study.brand}`}
              aspect="aspect-[16/8]"
            />
          )}
        </FadeUp>

        {study.sections.map((section, i) => (
          <section
            key={section.heading}
            className="grid gap-8 border-t border-line py-14 md:grid-cols-12 md:py-20"
          >
            <div className="md:col-span-4">
              <FadeUp>
                <p className="font-display text-4xl font-extrabold tracking-tight text-blue md:text-5xl">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="mt-4 font-display text-2xl font-bold tracking-tight md:text-3xl">
                  {section.heading}
                </h2>
              </FadeUp>
            </div>
            <div className="md:col-span-6 md:col-start-6">
              {section.body.map((p, j) => (
                <FadeUp key={j} delay={0.08 * j}>
                  <p className="mb-5 text-base leading-relaxed text-smoke md:text-lg">
                    {p}
                  </p>
                </FadeUp>
              ))}
              {i === 1 && (
                <FadeUp delay={0.15}>
                  <blockquote className="mt-8 border-l-2 border-blue pl-6 font-display text-xl font-semibold leading-snug tracking-tight text-ink md:text-2xl">
                    "{study.thought}"
                  </blockquote>
                </FadeUp>
              )}
            </div>
          </section>
        ))}

        {study.metrics && (
          <section className="border-t border-line py-14 md:py-20" data-testid="case-impact">
            <FadeUp>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue">
                Impact
              </p>
            </FadeUp>
            <div className="mt-8 grid grid-cols-2 gap-px border border-line bg-line md:grid-cols-5">
              {study.metrics.map((m) => (
                <FadeUp key={m.label} className="bg-paper p-6 md:p-8">
                  <p className="font-display text-2xl font-extrabold tracking-tight text-blue md:text-3xl">
                    {m.value}
                  </p>
                  <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-smoke">
                    {m.label}
                  </p>
                </FadeUp>
              ))}
            </div>
            {study.activities && (
              <div className="mt-12 grid gap-8 md:grid-cols-12">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke md:col-span-4">
                  Activity mix
                </p>
                <ul className="space-y-4 md:col-span-8">
                  {study.activities.map((a, i) => (
                    <FadeUp key={i} as="li" delay={0.05 * i} className="flex gap-4 border-b border-line pb-4">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-blue">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm leading-relaxed text-ink md:text-base">{a}</span>
                    </FadeUp>
                  ))}
                </ul>
              </div>
            )}
            {study.outcome && (
              <FadeUp delay={0.1}>
                <p className="mt-12 border-l-2 border-blue pl-6 font-display text-xl font-semibold leading-snug tracking-tight text-ink md:text-2xl">
                  {study.outcome}
                </p>
              </FadeUp>
            )}
          </section>
        )}

        {!study.image && (
          <FadeUp className="grid gap-6 pb-20 md:grid-cols-2">
            <ImageSlot label="On-ground · Frame 01" aspect="aspect-[4/3]" />
            <ImageSlot label="On-ground · Frame 02" aspect="aspect-[4/3]" />
          </FadeUp>
        )}
        {study.image && <div className="pb-20" />}
      </article>

      <Link
        to={`/work/${next.slug}`}
        data-testid="case-next-link"
        className="group block border-t border-ink bg-fog/60 transition-colors duration-300 hover:bg-fog"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-16 md:py-24">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
              Next case — {next.brand}
            </p>
            <p className="mt-4 font-display font-bold leading-[0.95] tracking-tight text-[clamp(2rem,6vw,5rem)] transition-colors duration-300 group-hover:text-blue">
              {next.project.toUpperCase()}
            </p>
          </div>
          <ArrowRight
            size={40}
            className="shrink-0 transition-transform duration-300 group-hover:translate-x-2 group-hover:text-blue"
          />
        </div>
      </Link>
    </div>
  );
}
